// LeadMemoryPanel — Phase 3.
//
// "AI remembers everything" — small human facts about the lead
// that make every next conversation feel personal. Stored as chips
// on lead.ai_memory[] (array of { text, added_at, source }).
//
// Persistence: onSetField({ ai_memory: [...] }) → setLeadField.
// No schema change. In Phase 6 the Digital Twin synthesis reads
// these + all the interaction history to produce a briefing that
// mentions them naturally.

import React from 'react';

function extractSuggestions(lead) {
  // Best-effort extraction from notes so operators don't stare at
  // an empty box. Every suggestion is quoted verbatim from notes so
  // there's zero hallucination risk.
  const notes = String(lead?.notes || '');
  if (!notes) return [];
  const facts = [];
  const patterns = [
    /(likes?|loves?|enjoys?)\s+[a-z\s]{3,40}/gi,
    /(daughter|son|wife|husband|kid|kids|dog|dogs|cat|cats)\s+[a-z\s]{3,40}/gi,
    /(birthday|anniversary|graduation)\s+[a-z\s\d]{3,40}/gi,
    /works?\s+(nights|days|graveyard|mornings|swing shift)/gi,
    /(coffee|golf|football|baseball|hunting|fishing|church|gym)/gi,
  ];
  for (const re of patterns) {
    for (const m of notes.matchAll(re)) {
      const txt = m[0].trim().replace(/[.,;]$/, '');
      if (txt.length >= 5 && txt.length <= 80 && !facts.includes(txt)) facts.push(txt);
    }
  }
  return facts.slice(0, 5);
}

export default function LeadMemoryPanel({ lead, onSetField }) {
  const memory = Array.isArray(lead?.ai_memory) ? lead.ai_memory : [];
  const [draft, setDraft] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);

  React.useEffect(() => {
    // Compute once per lead; suggestions are read-only hints.
    setSuggestions(extractSuggestions(lead).filter(s =>
      !memory.some(m => (m.text || '').toLowerCase() === s.toLowerCase())
    ));
  }, [lead, memory]);

  const add = (text, source = 'manual') => {
    const t = String(text || '').trim();
    if (!t) return;
    const next = [...memory, { text: t, added_at: new Date().toISOString(), source }];
    onSetField?.({ ai_memory: next });
    setDraft('');
  };

  const remove = (i) => {
    const next = memory.slice();
    next.splice(i, 1);
    onSetField?.({ ai_memory: next });
  };

  return (
    <div style={{
      background: '#faf5ff',
      border: '1px solid #e9d5ff',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#7c3aed', fontWeight: 800 }}>
          AI Memory
        </div>
        <div style={{ fontSize: 11, color: '#6b21a8' }}>
          {memory.length} fact{memory.length === 1 ? '' : 's'} the AI will remember
        </div>
      </div>

      {/* Memory chips */}
      {memory.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {memory.map((m, i) => (
            <div key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#ffffff', border: '1px solid #d8b4fe',
              padding: '4px 10px', borderRadius: 999, fontSize: 11,
              color: '#581c87',
            }}>
              <span>{m.text}</span>
              <button
                onClick={() => remove(i)}
                aria-label="Remove memory"
                style={{
                  background: 'transparent', border: 'none', color: '#a78bfa',
                  fontSize: 12, cursor: 'pointer', lineHeight: 1, padding: 0,
                }}
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* Add input */}
      <form onSubmit={(e) => { e.preventDefault(); add(draft); }} style={{ display: 'flex', gap: 6 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Daughter graduates next week"
          style={{
            flex: 1, background: '#ffffff', border: '1px solid #d8b4fe',
            borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#0f172a',
          }}
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          style={{
            background: '#7c3aed', border: 'none', color: '#ffffff',
            borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 800,
            cursor: draft.trim() ? 'pointer' : 'not-allowed', opacity: draft.trim() ? 1 : 0.5,
          }}
        >Add</button>
      </form>

      {/* Suggestions extracted from notes */}
      {suggestions.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 9, color: '#6b21a8', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>
            Suggested from notes
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {suggestions.map(s => (
              <button
                key={s}
                onClick={() => add(s, 'notes')}
                style={{
                  background: 'transparent', border: '1px dashed #c4b5fd',
                  color: '#6b21a8', borderRadius: 999, padding: '3px 10px',
                  fontSize: 11, cursor: 'pointer',
                }}
              >+ {s}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
